<template>
  <aside
    :class="[
      'bg-white shadow-md p-6 flex flex-col min-h-screen transition-all duration-300',
      sidebarOpen ? 'w-64' : 'w-20'
    ]"
  >
    <button
      @click="toggleSidebar"
      class="mb-4 flex items-center justify-center p-2 rounded hover:bg-gray-200"
    >
      <ChevronLeftIcon v-if="sidebarOpen" class="w-5 h-5" />
      <ChevronRightIcon v-else class="w-5 h-5" />
    </button>
    <!-- Title -->
    <h2 v-if="sidebarOpen" class="text-2xl font-bold mb-2">Dashboard</h2>
    
    <!-- Credential Switcher -->
    <CredentialSwitcher :sidebarOpen="sidebarOpen" />

    <nav class="flex-1">
      <ul class="space-y-2">
        <!-- Users Menu -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleUsers"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <UserIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Users
            </span>
            <ChevronRightIcon
              v-if="!usersOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="usersOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>
          <!-- Submenu -->
          <ul v-show="usersOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/users"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/users/create"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <PlusIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Create</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/users/0/goals"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <!-- reuse ListBullet icon for simplicity -->
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Goals</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Assessment Menu -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleAssessment"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <ClipboardDocumentCheckIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Assessment
            </span>
            <ChevronRightIcon
              v-if="!assessmentOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="assessmentOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="assessmentOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/users/0"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <UserIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">User</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/session-overview"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <CalendarDaysIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Session Overview</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/ai-feedback"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 flex-shrink-0">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
                <span class="ml-2">AI Feedback</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/prompt-config"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 flex-shrink-0">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="ml-2">Prompt Config</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Feedback Menu -->
        <li v-if="userType === 'student'">
          <router-link
            to="/users/0/feedback"
            :class="[
              'flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
            active-class="bg-gray-300 font-semibold"
          >
            <ClipboardDocumentCheckIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Feedback
            </span>
          </router-link>
        </li>

        <li v-if="userType === 'student'">
          <router-link
            to="/my-session-overview"
            :class="[
              'flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
            active-class="bg-gray-300 font-semibold"
          >
            <CalendarDaysIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Session Overview
            </span>
          </router-link>
        </li>

        <!-- Notification Menu -->
        <li v-if="userType === 'student'">
          <router-link
            to="/notifications"
            :class="[
              'flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
            active-class="bg-gray-300 font-semibold"
          >
            <BellIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Notifications
            </span>
          </router-link>
        </li>

        <!-- Roles Menu (NEW) -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleRoles"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <ShieldCheckIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Roles
            </span>
            <ChevronRightIcon
              v-if="!rolesOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="rolesOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="rolesOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/roles"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/roles/create"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <PlusIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Create</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Teams Menu -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleTeams"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <UserGroupIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Teams
            </span>
            <ChevronRightIcon
              v-if="!teamsOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="teamsOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="teamsOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/teams"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/teams/create"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <PlusIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Create</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Criterias Menu -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleCriterias"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <ClipboardDocumentListIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Criterias
            </span>
            <ChevronRightIcon
              v-if="!criteriasOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="criteriasOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="criteriasOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/criterias"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/criterias/create"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <PlusIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Create</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Sessions Menu -->
        <li v-if="userType === 'professor'">
          <button
            @click="toggleSessions"
            :class="[
              'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
          >
            <CalendarDaysIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Sessions
            </span>
            <ChevronRightIcon
              v-if="!sessionsOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
              v-if="sessionsOpen && sidebarOpen"
              class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="sessionsOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                to="/sessions"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/sessions/create"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <PlusIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Create</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/sessions/team-templates"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="ml-2">Team Templates</span>
              </router-link>
            </li>
            <li>
              <router-link
                to="/sessions/assign-teams"
                class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                active-class="bg-gray-300 font-semibold"
              >
                <UserGroupIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Assign Teams</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Async Messages Menu -->
        <li v-if="userType === 'professor'">
          <button
              @click="toggleAsyncSessions"
              :class="[
                'w-full flex items-center rounded hover:bg-gray-200 transition-colors',
                sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
              ]"
          >
            <ClockIcon class="w-5 h-5 flex-shrink-0" />
            <span
                :class="[
        'ml-2 whitespace-nowrap transition-opacity duration-200',
        sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
      ]"
            >
      Async Messages
    </span>
            <ChevronRightIcon
                v-if="!asyncSessionsOpen && sidebarOpen"
                class="ml-auto w-4 h-4 transition-transform"
            />
            <ChevronDownIcon
                v-if="asyncSessionsOpen && sidebarOpen"
                class="ml-auto w-4 h-4 transition-transform"
            />
          </button>

          <!-- Submenu -->
          <ul v-show="asyncSessionsOpen && sidebarOpen" class="mt-2 ml-8 space-y-1">
            <li>
              <router-link
                  to="/async/messages"
                  class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                  active-class="bg-gray-300 font-semibold"
              >
                <ListBulletIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">List</span>
              </router-link>
            </li>
            <li>
              <router-link
                  to="/async/prompt-versions"
                  class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                  active-class="bg-gray-300 font-semibold"
              >
                <DocumentDuplicateIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Prompt Versions</span>
              </router-link>
            </li>
            <li>
              <router-link
                  to="/async/reflection"
                  class="flex items-center px-4 py-2 rounded hover:bg-gray-200"
                  active-class="bg-gray-300 font-semibold"
              >
                <ArrowPathRoundedSquareIcon class="w-5 h-5 flex-shrink-0" />
                <span class="ml-2">Reflection</span>
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Files menu -->
        <li v-if="userType === 'professor' || userType === 'admin'">
          <router-link
            to="/files"
            :class="[
              'flex items-center rounded hover:bg-gray-200 transition-colors',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
            active-class="bg-gray-300 font-semibold"
          >
            <ArrowUpTrayIcon class="w-5 h-5 flex-shrink-0" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Files
            </span>
          </router-link>
        </li>

        <!-- Admin Menu -->
        <li v-if="userType === 'admin'">
          <router-link
            to="/admin"
            :class="[
              'flex items-center rounded hover:bg-red-100 transition-colors border-2 border-red-300',
              sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
            ]"
            active-class="bg-red-200 font-semibold"
          >
            <ShieldCheckIcon class="w-5 h-5 flex-shrink-0 text-red-600" />
            <span
              :class="[
                'ml-2 whitespace-nowrap transition-opacity duration-200 text-red-600 font-semibold',
                sidebarOpen ? 'opacity-100' : 'opacity-0 hidden'
              ]"
            >
              Admin Panel
            </span>
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- User Info -->
    <div v-if="auth.user.value" class="mt-auto mb-4 border-t pt-4">
      <div v-if="sidebarOpen" class="px-2">
        <div class="text-sm font-semibold text-gray-700 mb-1">
          {{ auth.user.value.first_name }} {{ auth.user.value.last_name }}
        </div>
        <div class="text-xs text-gray-500">
          {{ auth.user.value.email }}
        </div>
        <div class="text-xs text-blue-600 font-medium mt-1">
          {{ auth.user.value.user_type }}
        </div>
      </div>
      <div v-else class="flex justify-center">
        <UserIcon class="w-6 h-6 text-gray-600" />
      </div>
    </div>

    <!-- Logout Button -->
    <button
      @click="logout"
      :class="[
        'flex items-center bg-red-500 text-white rounded hover:bg-red-600 transition-colors',
        sidebarOpen ? 'px-4 py-2 justify-start' : 'p-2 justify-center'
      ]"
    >
      <ArrowRightOnRectangleIcon class="w-5 h-5 flex-shrink-0" />
      <span v-if="sidebarOpen" class="ml-2">Logout</span>
    </button>
  </aside>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useAuth } from "@/stores/auth";
import { useRouter } from "vue-router";
import CredentialSwitcher from "@/BaseComponents/CredentialSwitcher.vue";
// Heroicons
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  ListBulletIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ArrowPathRoundedSquareIcon,
  ArrowRightOnRectangleIcon,
  BellIcon
} from "@heroicons/vue/24/outline";

const sidebarOpen = ref(true);
const usersOpen = ref(false);
const assessmentOpen = ref(false);
const teamsOpen = ref(false);
const criteriasOpen = ref(false);
const sessionsOpen = ref(false);
const rolesOpen = ref(false);
const asyncSessionsOpen = ref(false);

const auth = useAuth();
const router = useRouter();

console.log(auth)
const userType = computed(() => auth.user.value?.user_type?.toLowerCase() || null);

const handleResize = () => {
  if (localStorage.getItem("sidebarOpen") === null) {
    sidebarOpen.value = window.innerWidth >= 1024;
  }
};

watch(sidebarOpen, (newValue) => {
  localStorage.setItem("sidebarOpen", newValue);
});

onMounted(() => {
  const savedState = localStorage.getItem("sidebarOpen");

  if (savedState !== null) {
    sidebarOpen.value = savedState === "true";
  } else {
    sidebarOpen.value = window.innerWidth >= 1024;
  }

  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
  localStorage.setItem("sidebarOpen", sidebarOpen.value);
};

const toggleUsers = () => (usersOpen.value = !usersOpen.value);
const toggleAssessment = () => (assessmentOpen.value = !assessmentOpen.value);
const toggleRoles = () => (rolesOpen.value = !rolesOpen.value);
const toggleTeams = () => (teamsOpen.value = !teamsOpen.value);
const toggleCriterias = () => (criteriasOpen.value = !criteriasOpen.value);
const toggleSessions = () => (sessionsOpen.value = !sessionsOpen.value);
const toggleAsyncSessions = () => (asyncSessionsOpen.value = !asyncSessionsOpen.value);

const logout = async () => {
  auth.logout();
  // Only end our own session; skip the IdP logout redirect for now.
  router.push("/login");
};

</script>
